import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private readonly tokenKey = 'authToken';
  private readonly accessTokenKey = 'accessToken';
  private readonly refreshTokenKey = 'refreshToken';
  private readonly rolesKey = 'assignedRoles';
  private readonly userIdKey = 'userId';
  private readonly firstLoginKey = 'firstLoginRequired';
  private readonly authMethodsKey = 'authMethods';

  getToken(): string | undefined {
    const token = localStorage.getItem(this.tokenKey);
    return token ?? undefined;
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  setAccessToken(token: string): void {
    localStorage.setItem(this.accessTokenKey, token);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  setUserId(userId: string): void {
    localStorage.setItem(this.userIdKey, userId);
  }

  getUserId(): string | undefined {
    const userId = localStorage.getItem(this.userIdKey);
    return userId ?? undefined;
  }

  setRoles(roles: string[]): void {
    localStorage.setItem(this.rolesKey, JSON.stringify(roles));
  }

  getRoles(): string[] {
    const raw = localStorage.getItem(this.rolesKey);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  setFirstLoginRequired(required: boolean): void {
    localStorage.setItem(this.firstLoginKey, required ? 'true' : 'false');
  }

  isFirstLoginRequired(): boolean {
    return localStorage.getItem(this.firstLoginKey) === 'true';
  }

  setAuthMethods(methods: string[]): void {
    localStorage.setItem(this.authMethodsKey, JSON.stringify(methods));
  }

  getAuthMethods(): string[] {
    const raw = localStorage.getItem(this.authMethodsKey);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  hasPasswordAuth(): boolean {
    return this.getAuthMethods().includes('password');
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.rolesKey);
    localStorage.removeItem(this.userIdKey);
    localStorage.removeItem(this.firstLoginKey);
    localStorage.removeItem(this.authMethodsKey);
  }
}
